import { Grid, Radio, RadioGroup, useEventCallback } from '@mui/material';
import { memo, useCallback } from 'react';
import { FormikErrors, FormikTouched } from 'formik';
import { SectionVM } from 'civil-status/view-models/step-form';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import { FormControlLabel } from '@mui/material';
import { FormControl } from '@mui/material';
import Italian from '../../shared/italian';
import Foreign from '../../shared/foreign';
import { ItalianForm } from 'shell/view-models/italian-form';
import { ForeignForm } from 'civil-status/view-models/accordion-generality-form';
import ListAnsc from '../../../../../../shared/list-ansc';
import { ItemVM } from 'shell/view-models/item';

interface CitizenshipDpImpProps {
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
}

const CitizenshipDpImpComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  onChanges,
}: CitizenshipDpImpProps): React.ReactElement => {
  const handleListHiringTiming = useEventCallback((item?: ItemVM) => {
    onChange('hiringTiming', item?.id);
  });

  const onChangeItalian = useCallback(
    (key, value) => {
      onChange('italian', {
        ...(values || {}).italian,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onChangesItalian = useCallback(
    (valuesNew) => {
      onChange('italian', {
        ...(values || {}).italian,
        ...valuesNew,
      });
    },
    [values, onChange]
  );

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChange('foreign', {
        ...(values || {}).foreign,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onFieldsForeignChange = useCallback(
    (valuesNew) => {
      onChange('foreign', {
        ...(valuesNew || {}),
      });
    },
    [values, onChange]
  );

  return (
    <Grid container spacing={2} item>
      <Grid item xs={12} md={4}>
        <ListAnsc
          label="Tempistica assunzione"
          idDominio="45"
          value={values?.hiringTiming || ''}
          onChange={handleListHiringTiming}
          disabled={disabled}
          error={Boolean(errors?.hiringTiming) && touched?.hiringTiming}
          required
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data formazione documento *"
          aria-label="Data formazione documento"
          value={values?.documentDateFormation || null}
          onChange={(date) => {
            onChange('documentDateFormation', date);
          }}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="documentDateFormation"
              fullWidth
              helperText={errors?.documentDateFormation}
              error={touched?.documentDateFormation && Boolean(errors?.documentDateFormation)}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Estremi documento *"
          aria-label="Estremi documento"
          id="documentExtremes"
          name="documentExtremes"
          fullWidth
          disabled={disabled}
          helperText={errors?.documentExtremes}
          error={touched?.documentExtremes && Boolean(errors?.documentExtremes)}
          type="text"
          variant="outlined"
          multiline
          value={values?.documentExtremes || ''}
          onChange={(event) => {
            onChange('documentExtremes', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Nome ente *"
          aria-label="Nome ente"
          id="nameInstitution"
          name="nameInstitution"
          fullWidth
          disabled={disabled}
          helperText={errors?.nameInstitution}
          error={touched?.nameInstitution && Boolean(errors?.nameInstitution)}
          type="text"
          variant="outlined"
          value={values?.nameInstitution || ''}
          onChange={(event) => {
            onChange('nameInstitution', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Stato"
            id="isForeign"
            value={values?.isForeign || 'false'}
            onChange={() => {
              onChanges({
                ...(values || {}),
                isForeign: !values?.isForeign,
                italian: {
                  location: '',
                  province: '',
                  municipality: null,
                },
                foreign: {
                  state: null,
                  province: '',
                  municipality: null,
                },
              });
            }}
          >
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Italia"
              disabled={disabled}
            />
            <FormControlLabel value="true" control={<Radio />} label="Estero" disabled={disabled} />
          </RadioGroup>
        </FormControl>
      </Grid>
      {!values?.isForeign ? (
        <Italian
          disabledComponent={disabled}
          values={values?.italian}
          onChange={onChangeItalian}
          onChanges={onChangesItalian}
          errors={errors?.italian as FormikErrors<ItalianForm>}
          touched={touched?.italian as unknown as FormikTouched<ItalianForm>}
          labelMunicipality="Comune ente"
          labelProvince="Provincia ente"
          required
          fromDate={values?.documentDateFormation || null}
        />
      ) : (
        <Foreign
          disabledComponent={disabled}
          values={values?.foreign}
          onChange={onFieldForeignChange}
          onChanges={onFieldsForeignChange}
          labelState="Stato ente"
          errors={errors?.foreign as FormikErrors<ForeignForm>}
          touched={touched?.foreign as unknown as FormikTouched<ForeignForm>}
          isWithLocationForeign
        />
      )}
    </Grid>
  );
};

CitizenshipDpImpComponent.displayName = 'Form Citizenship DpImp component';

const CitizenshipDpImp = memo(CitizenshipDpImpComponent);

export default CitizenshipDpImp;
